import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMe } from '../api/useMe';
import {
  useOrganizations,
  useOrganizationMembers,
  useInviteOrganizationMember,
  useUpdateOrganizationMember,
} from '../api/useOrganizations';
import { useGroups, useCreateGroup, useInviteGroupMember } from '../api/useGroups';
import {
  useApiKeys,
  useCreateApiKey,
  useDeleteApiKey,
  useMcpTokens,
  useCreateMcpToken,
  useDeleteMcpToken,
} from '../api/useDeveloperSettings';
import McpAuditLogTable from '../components/settings/McpAuditLogTable';

type SettingsTab = 'profile' | 'team' | 'groups' | 'developer' | 'billing';

export default function SettingsPage() {
  const { data: me } = useMe();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as SettingsTab;
  const activeTab: SettingsTab =
    tabParam === 'team' ||
    tabParam === 'groups' ||
    tabParam === 'developer' ||
    tabParam === 'billing'
      ? tabParam
      : 'profile';

  const setActiveTab = (tab: SettingsTab) => {
    setSearchParams({ tab });
  };

  // Profile Form State
  const [firstName, setFirstName] = useState('Francisco');
  const [lastName, setLastName] = useState('Seruya');
  const [email] = useState('francisco@autonomousworld.pt');
  const [companyName, setCompanyName] = useState('Autonomous World, Lda');
  const [vatNumber, setVatNumber] = useState('518000290');
  const [website, setWebsite] = useState('https://www.autonomousworld.pt');
  const [description, setDescription] = useState(
    'The company provides bespoke consultancy and high-performance software solutions for B2B clients, focusing on data intelligence, AI integration, and automation.'
  );
  const [savedNotice, setSavedNotice] = useState(false);

  // Organizations & Team State
  const { data: orgs } = useOrganizations();
  const selectedOrgId = orgs && orgs.length > 0 ? orgs[0].id : undefined;
  const { data: members, isLoading: membersLoading } = useOrganizationMembers(selectedOrgId);
  const inviteOrgMemberMutation = useInviteOrganizationMember();
  const updateOrgMemberMutation = useUpdateOrganizationMember();

  const [isInviteOrgModalOpen, setIsInviteOrgModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'billing' | 'member'>('member');

  // Groups State
  const { data: groups, isLoading: groupsLoading } = useGroups();
  const createGroupMutation = useCreateGroup();
  const inviteGroupMemberMutation = useInviteGroupMember();

  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupDesc, setGroupDesc] = useState('');
  const [groupNeverExpires, setGroupNeverExpires] = useState(false);

  const [inviteGroupId, setInviteGroupId] = useState<string | null>(null);
  const [groupInviteEmail, setGroupInviteEmail] = useState('');
  const [groupInviteRole, setGroupInviteRole] = useState<'member' | 'viewer' | 'admin'>('member');

  // Developer Settings (API Keys & MCP Tokens) State
  const { data: apiKeys, isLoading: apiKeysLoading } = useApiKeys();
  const createApiKeyMutation = useCreateApiKey();
  const deleteApiKeyMutation = useDeleteApiKey();

  const { data: mcpTokens, isLoading: mcpTokensLoading } = useMcpTokens();
  const createMcpTokenMutation = useCreateMcpToken();
  const deleteMcpTokenMutation = useDeleteMcpToken();

  const [isCreateApiKeyModalOpen, setIsCreateApiKeyModalOpen] = useState(false);
  const [apiKeyName, setApiKeyName] = useState('');
  const [apiKeyExpiry, setApiKeyExpiry] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d.toISOString().slice(0, 10);
  });
  const [createdKeySecret, setCreatedKeySecret] = useState<string | null>(null);
  const [copiedKey, setCopiedKey] = useState(false);

  const [keyToDelete, setKeyToDelete] = useState<{ id: string; name: string } | null>(null);

  const [isCreateMcpModalOpen, setIsCreateMcpModalOpen] = useState(false);
  const [mcpTokenName, setMcpTokenName] = useState('');
  const [createdMcpSecret, setCreatedMcpSecret] = useState<string | null>(null);

  const userPlan = me?.subscription?.plan_name || 'AutWorld';
  const parsesLeft = me?.usage?.quota_limit
    ? Math.max(0, me.usage.quota_limit - (me.usage.parsed_count || 0))
    : 802;
  const totalParses = me?.usage?.quota_limit || 999;

  const handleProfileSave = (e: React.FormEvent) => {
    e.preventDefault();
    setSavedNotice(true);
    setTimeout(() => setSavedNotice(false), 3000);
  };

  const handleInviteOrgSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrgId || !inviteEmail) return;
    await inviteOrgMemberMutation.mutateAsync({
      orgId: selectedOrgId,
      payload: { email: inviteEmail, role: inviteRole },
    });
    setInviteEmail('');
    setIsInviteOrgModalOpen(false);
  };

  const handleCreateGroupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupName.trim()) return;
    await createGroupMutation.mutateAsync({
      name: groupName.trim(),
      description: groupDesc.trim(),
      never_expires: groupNeverExpires,
    });
    setGroupName('');
    setGroupDesc('');
    setGroupNeverExpires(false);
    setIsCreateGroupModalOpen(false);
  };

  const handleInviteGroupSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteGroupId || !groupInviteEmail) return;
    await inviteGroupMemberMutation.mutateAsync({
      groupId: inviteGroupId,
      payload: { email: groupInviteEmail, role: groupInviteRole },
    });
    setGroupInviteEmail('');
    setInviteGroupId(null);
  };

  const handleCreateApiKeySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKeyName.trim()) return;
    const res = await createApiKeyMutation.mutateAsync({
      name: apiKeyName.trim(),
      expiration_date: apiKeyExpiry,
    });
    setCreatedKeySecret(res.key);
    setApiKeyName('');
  };

  const handleDeleteApiKeyConfirm = async () => {
    if (!keyToDelete) return;
    await deleteApiKeyMutation.mutateAsync(keyToDelete.id);
    setKeyToDelete(null);
  };

  const handleCreateMcpTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mcpTokenName.trim()) return;
    const res = await createMcpTokenMutation.mutateAsync({
      name: mcpTokenName.trim(),
    });
    setCreatedMcpSecret(res.token);
    setMcpTokenName('');
  };

  return (
    <div className="max-w-[1180px] mx-auto py-2 sm:py-6 px-2 sm:px-4">
      {/* Eyebrow & Title */}
      <div className="mb-6">
        <div className="text-[11.5px] font-mono font-semibold tracking-wider text-brand-vib uppercase dark:text-brand-light">
          / ACCOUNT & WORKSPACE
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-brand-navy dark:text-white tracking-tight mt-0.5">
          Settings
        </h1>
        <p className="text-xs sm:text-sm text-brand-muted dark:text-bodydark mt-1">
          Manage your organization, team members, sharing groups, and developer API keys.
        </p>
      </div>

      {/* Plan & Usage Banner */}
      <div className="rounded-2.5xl hero-gradient p-5 sm:p-6 mb-8 text-white shadow-hero flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
          <div>
            <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-brand-light">
              CURRENT PLAN
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white font-mono mt-0.5">
              {userPlan}
            </div>
          </div>

          <div className="sm:border-l sm:border-white/15 sm:pl-10">
            <div className="text-[10px] font-mono font-bold uppercase tracking-wider text-brand-light">
              USAGE THIS PERIOD
            </div>
            <div className="text-sm sm:text-base font-mono font-semibold text-white mt-0.5 flex items-center gap-2">
              <span className="text-brand-green font-bold">{parsesLeft}</span>
              <span className="text-white/60">/ {totalParses} parses left</span>
              <span className="text-xs text-white/50">· renews 2026-12-31</span>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setActiveTab('billing')}
          className="px-5 py-2.5 rounded-xl font-mono text-xs font-bold uppercase tracking-wider bg-brand-green text-brand-navy hover:bg-[#0fd48d] transition-colors shadow-sm inline-flex items-center justify-center gap-2 flex-shrink-0 cursor-pointer"
        >
          Manage Plan
        </button>
      </div>

      {/* TAB 1: Profile & Details */}
      {activeTab === 'profile' && (
        <form onSubmit={handleProfileSave} className="space-y-6">
          <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-6 sm:p-7 shadow-card">
            <div className="flex items-center gap-2 pb-4 mb-6 border-b border-brand-line dark:border-strokedark">
              <div className="w-2 h-2 rounded-full bg-brand-vib" />
              <h2 className="text-xs font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white">
                Your Details
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-[11px] font-mono font-bold tracking-wider text-brand-soft dark:text-bodydark2 uppercase mb-1.5">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono font-bold tracking-wider text-brand-soft dark:text-bodydark2 uppercase mb-1.5">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono font-bold tracking-wider text-brand-soft dark:text-bodydark2 uppercase mb-1.5">
                  Email Address (Read-only)
                </label>
                <input
                  type="email"
                  disabled
                  value={email}
                  className="w-full px-4 py-2.5 rounded-xl bg-brand-bg/50 dark:bg-strokedark/30 border border-brand-line dark:border-strokedark text-sm text-brand-muted dark:text-bodydark cursor-not-allowed shadow-xs"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono font-bold tracking-wider text-brand-soft dark:text-bodydark2 uppercase mb-1.5">
                  Company Name
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono font-bold tracking-wider text-brand-soft dark:text-bodydark2 uppercase mb-1.5">
                  VAT Number
                </label>
                <input
                  type="text"
                  value={vatNumber}
                  onChange={(e) => setVatNumber(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono font-bold tracking-wider text-brand-soft dark:text-bodydark2 uppercase mb-1.5">
                  Website
                </label>
                <input
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs"
                />
              </div>
            </div>
          </div>

          <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-6 sm:p-7 shadow-card">
            <div className="flex items-center gap-2 pb-4 mb-4 border-b border-brand-line dark:border-strokedark">
              <div className="w-2 h-2 rounded-full bg-brand-light" />
              <h2 className="text-xs font-mono font-bold uppercase tracking-wider text-brand-navy dark:text-white">
                Bookkeeping Intelligence
              </h2>
            </div>
            <p className="text-xs text-brand-muted dark:text-bodydark mb-3">
              Describe your core business. The AI uses this context to automatically categorize incoming line items and supplier expenses.
            </p>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib shadow-xs"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            {savedNotice ? (
              <span className="text-xs font-mono font-semibold text-brand-green">
                ✓ Changes saved successfully!
              </span>
            ) : (
              <span />
            )}
            <button
              type="submit"
              className="px-6 py-2.5 rounded-xl text-xs sm:text-sm font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover shadow-sm transition-colors cursor-pointer"
            >
              Save Changes
            </button>
          </div>
        </form>
      )}

      {/* TAB 2: Team & Members */}
      {activeTab === 'team' && (
        <div className="space-y-6">
          <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-6 sm:p-7 shadow-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 mb-6 border-b border-brand-line dark:border-strokedark">
              <div>
                <h2 className="text-lg font-bold text-brand-navy dark:text-white tracking-tight">
                  Team Members
                </h2>
                <p className="text-xs text-brand-muted dark:text-bodydark mt-0.5">
                  Manage members who have access to your organization&rsquo;s invoices.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsInviteOrgModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover transition-colors shadow-sm cursor-pointer"
              >
                <span>+ Invite Member</span>
              </button>
            </div>

            {membersLoading ? (
              <div className="py-8 text-center text-xs font-mono text-brand-muted">Loading team roster…</div>
            ) : !members || members.length === 0 ? (
              <div className="py-8 text-center text-xs font-mono text-brand-muted">No team members registered yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-brand-blue-tint dark:bg-brand-900/30 border-b border-brand-line dark:border-strokedark text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300">
                      <th className="py-3 px-4">Member Email</th>
                      <th className="py-3 px-4">Role</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-line dark:divide-strokedark text-xs font-mono">
                    {members.map((m) => (
                      <tr key={m.id} className="hover:bg-brand-bg/40 transition-colors">
                        <td className="py-3.5 px-4 font-semibold text-brand-navy dark:text-white">
                          {m.email} {m.is_owner && <span className="ml-1 text-[10px] text-brand-vib">(Owner)</span>}
                        </td>
                        <td className="py-3.5 px-4">
                          <span className="capitalize px-2 py-0.5 rounded-full bg-brand-bg dark:bg-strokedark text-brand-navy dark:text-white">
                            {m.role}
                          </span>
                        </td>
                        <td className="py-3.5 px-4">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              m.status === 'active'
                                ? 'bg-brand-green-tint text-[#0a6b4a]'
                                : m.status === 'invited'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-rose-100 text-rose-800'
                            }`}
                          >
                            {m.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          {!m.is_owner && (
                            <div className="inline-flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  updateOrgMemberMutation.mutate({
                                    orgId: selectedOrgId || '',
                                    memberId: m.id,
                                    payload: { status: m.status === 'blocked' ? 'active' : 'blocked' },
                                  })
                                }
                                className="text-[11px] text-brand-soft hover:text-rose-600 transition-colors cursor-pointer"
                              >
                                {m.status === 'blocked' ? 'Unblock' : 'Block'}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 3: Sharing Groups */}
      {activeTab === 'groups' && (
        <div className="space-y-6">
          <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-6 sm:p-7 shadow-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 mb-6 border-b border-brand-line dark:border-strokedark">
              <div>
                <h2 className="text-lg font-bold text-brand-navy dark:text-white tracking-tight">
                  Organizational Sharing Groups
                </h2>
                <p className="text-xs text-brand-muted dark:text-bodydark mt-0.5">
                  Create groups to share batches of invoices with accountants, auditors, or department heads.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsCreateGroupModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover transition-colors shadow-sm cursor-pointer"
              >
                <span>+ Create Group</span>
              </button>
            </div>

            {groupsLoading ? (
              <div className="py-8 text-center text-xs font-mono text-brand-muted">Loading groups…</div>
            ) : !groups || groups.length === 0 ? (
              <div className="py-8 text-center text-xs font-mono text-brand-muted">No sharing groups created yet.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {groups.map((g) => (
                  <div
                    key={g.id}
                    className="p-4 rounded-2xl bg-brand-bg/60 dark:bg-strokedark/30 border border-brand-line dark:border-strokedark flex flex-col justify-between gap-3"
                  >
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-sm font-bold text-brand-navy dark:text-white font-mono">
                            {g.name}
                          </h3>
                          {(g.organization_name || (typeof g.organization === 'object' && g.organization?.name)) && (
                            <span className="text-[10px] font-mono text-brand-soft dark:text-bodydark2 px-2 py-0.5 rounded-md bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark">
                              {g.organization_name || (typeof g.organization === 'object' && g.organization?.name)}
                            </span>
                          )}
                        </div>
                        <span
                          className={`text-[10px] font-mono px-2 py-0.5 rounded-full font-semibold ${
                            g.never_expires
                              ? 'bg-brand-green-tint text-[#0a6b4a]'
                              : 'bg-brand-blue-tint text-brand-vib'
                          }`}
                        >
                          {g.never_expires ? 'Permanent Share' : '7-Day Window'}
                        </span>
                      </div>
                      {g.description && (
                        <p className="text-xs text-brand-muted dark:text-bodydark mt-1">
                          {g.description}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-brand-line/60 dark:border-strokedark text-xs font-mono">
                      <span className="text-brand-soft">
                        {g.members_count || 0} members
                      </span>
                      <button
                        type="button"
                        onClick={() => setInviteGroupId(g.id)}
                        className="text-brand-vib font-semibold hover:underline cursor-pointer"
                      >
                        + Invite to Group
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 4: APIs & MCP Tokens */}
      {activeTab === 'developer' && (
        <div className="space-y-6">
          {/* Developer API Keys Section */}
          <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-6 sm:p-7 shadow-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 mb-6 border-b border-brand-line dark:border-strokedark">
              <div>
                <h2 className="text-lg font-bold text-brand-navy dark:text-white tracking-tight">
                  Developer REST API Keys
                </h2>
                <p className="text-xs text-brand-muted dark:text-bodydark mt-0.5">
                  Generate signed JWT access keys to parse invoices programmatically.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsCreateApiKeyModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover transition-colors shadow-sm cursor-pointer"
              >
                <span>+ Generate API Key</span>
              </button>
            </div>

            {apiKeysLoading ? (
              <div className="py-8 text-center text-xs font-mono text-brand-muted">Loading API keys…</div>
            ) : !apiKeys || apiKeys.length === 0 ? (
              <div className="py-8 text-center text-xs font-mono text-brand-muted">No API keys created yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-brand-blue-tint dark:bg-brand-900/30 border-b border-brand-line dark:border-strokedark text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300">
                      <th className="py-3 px-4">Key Name</th>
                      <th className="py-3 px-4">Key Preview</th>
                      <th className="py-3 px-4">Expires</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-line dark:divide-strokedark text-xs font-mono">
                    {apiKeys.map((key) => (
                      <tr key={key.id} className="hover:bg-brand-bg/40 transition-colors">
                        <td className="py-3.5 px-4 font-semibold text-brand-navy dark:text-white">
                          {key.name}
                        </td>
                        <td className="py-3.5 px-4 text-brand-muted font-mono">
                          {key.key_preview}
                        </td>
                        <td className="py-3.5 px-4">
                          <span className={key.is_expired ? 'text-rose-600 font-bold' : 'text-brand-navy dark:text-white'}>
                            {key.expiration_date} {key.is_expired && '(Expired)'}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <button
                            type="button"
                            onClick={() => setKeyToDelete({ id: key.id, name: key.name })}
                            className="text-brand-soft hover:text-rose-600 transition-colors cursor-pointer"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* MCP Client Tokens Section */}
          <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-6 sm:p-7 shadow-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 mb-6 border-b border-brand-line dark:border-strokedark">
              <div>
                <h2 className="text-lg font-bold text-brand-navy dark:text-white tracking-tight">
                  Model Context Protocol (MCP) Client Tokens
                </h2>
                <p className="text-xs text-brand-muted dark:text-bodydark mt-0.5">
                  Generate secure tokens to connect Claude Desktop, Cursor, or your AI agents.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsCreateMcpModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-brand-blue-tint text-brand-vib hover:bg-brand-vib hover:text-white dark:bg-brand-900/40 dark:text-brand-300 dark:hover:bg-brand-vib dark:hover:text-white border border-brand-vib/20 transition-colors shadow-sm cursor-pointer"
              >
                <span>+ Create MCP Token</span>
              </button>
            </div>

            {mcpTokensLoading ? (
              <div className="py-8 text-center text-xs font-mono text-brand-muted">Loading MCP tokens…</div>
            ) : !mcpTokens || mcpTokens.length === 0 ? (
              <div className="py-8 text-center text-xs font-mono text-brand-muted">No MCP tokens created yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-brand-blue-tint dark:bg-brand-900/30 border-b border-brand-line dark:border-strokedark text-[11px] font-mono font-bold uppercase text-brand-vib dark:text-brand-300">
                      <th className="py-3 px-4">Token Name</th>
                      <th className="py-3 px-4">Prefix</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-line dark:divide-strokedark text-xs font-mono">
                    {mcpTokens.map((t) => (
                      <tr key={t.id} className="hover:bg-brand-bg/40 transition-colors">
                        <td className="py-3.5 px-4 font-semibold text-brand-navy dark:text-white">
                          {t.name}
                        </td>
                        <td className="py-3.5 px-4 font-mono text-brand-muted">
                          {t.token_prefix}…
                        </td>
                        <td className="py-3.5 px-4">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              t.status === 'active'
                                ? 'bg-brand-green-tint text-[#0a6b4a]'
                                : 'bg-rose-100 text-rose-800'
                            }`}
                          >
                            {t.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          {t.status === 'active' && (
                            <button
                              type="button"
                              onClick={() => deleteMcpTokenMutation.mutate(t.id)}
                              className="text-brand-soft hover:text-rose-600 transition-colors cursor-pointer"
                            >
                              Revoke
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Activity & Security Audit Log Table */}
          <McpAuditLogTable />
        </div>
      )}

      {/* TAB 5: Billing & Plan */}
      {activeTab === 'billing' && (
        <div className="space-y-6">
          <div className="rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark p-6 sm:p-7 shadow-card">
            <h2 className="text-lg font-bold text-brand-navy dark:text-white tracking-tight mb-2">
              Subscription & Invoicing
            </h2>
            <p className="text-xs sm:text-sm text-brand-muted dark:text-bodydark mb-6">
              Your plan gives you full access to OCR extraction, MCP Agent SSE endpoints, and developer API integrations.
            </p>
            <div className="p-5 rounded-2xl bg-brand-bg/60 dark:bg-strokedark/30 border border-brand-line dark:border-strokedark flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <div className="text-base font-bold text-brand-navy dark:text-white">
                  Autonomous World Enterprise Plan
                </div>
                <div className="text-xs font-mono text-brand-muted mt-0.5">
                  €99.00 / month · Renews on December 31, 2026
                </div>
              </div>
              <button
                type="button"
                onClick={() => alert('Redirecting to billing portal...')}
                className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover transition-colors shadow-sm cursor-pointer"
              >
                Change Billing Details
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 1: Invite Team Member */}
      {isInviteOrgModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-2xl p-6 relative">
            <h3 className="text-lg font-bold text-brand-navy dark:text-white mb-1">
              Invite Team Member
            </h3>
            <p className="text-xs text-brand-muted dark:text-bodydark mb-4">
              An invitation email will be sent. Member activates their account upon accepting.
            </p>
            <form onSubmit={handleInviteOrgSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-mono font-bold text-brand-soft uppercase mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib"
                />
              </div>
              <div>
                <label className="block text-xs font-mono font-bold text-brand-soft uppercase mb-1">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'admin' | 'billing' | 'member')}
                  className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib"
                >
                  <option value="member">Member (View & upload invoices)</option>
                  <option value="billing">Billing (Manage plans & invoices)</option>
                  <option value="admin">Admin (Manage team & settings)</option>
                </select>
              </div>
              <div className="flex items-center justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setIsInviteOrgModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-brand-navy dark:text-white border border-brand-line dark:border-strokedark"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteOrgMemberMutation.isPending}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover"
                >
                  {inviteOrgMemberMutation.isPending ? 'Sending…' : 'Send Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: Create Group */}
      {isCreateGroupModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-2xl p-6 relative">
            <h3 className="text-lg font-bold text-brand-navy dark:text-white mb-1">
              Create Sharing Group
            </h3>
            <p className="text-xs text-brand-muted dark:text-bodydark mb-4">
              Group invoices together for scoped sharing with external teams.
            </p>
            <form onSubmit={handleCreateGroupSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-mono font-bold text-brand-soft uppercase mb-1">
                  Group Name
                </label>
                <input
                  type="text"
                  required
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="e.g. Auditores Externos"
                  className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib"
                />
              </div>
              <div>
                <label className="block text-xs font-mono font-bold text-brand-soft uppercase mb-1">
                  Description (Optional)
                </label>
                <input
                  type="text"
                  value={groupDesc}
                  onChange={(e) => setGroupDesc(e.target.value)}
                  placeholder="e.g. Q3 Auditing batch"
                  className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib"
                />
              </div>
              <div className="flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="never_expires"
                  checked={groupNeverExpires}
                  onChange={(e) => setGroupNeverExpires(e.target.checked)}
                  className="w-4 h-4 rounded text-brand-vib focus:ring-brand-vib"
                />
                <label htmlFor="never_expires" className="text-xs text-brand-navy dark:text-white">
                  Permanent Sharing (Never Expires)
                </label>
              </div>
              <div className="flex items-center justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setIsCreateGroupModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-brand-navy dark:text-white border border-brand-line dark:border-strokedark"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createGroupMutation.isPending}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover"
                >
                  {createGroupMutation.isPending ? 'Creating…' : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: Invite to Group */}
      {inviteGroupId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-2xl p-6 relative">
            <h3 className="text-lg font-bold text-brand-navy dark:text-white mb-1">
              Invite to Group
            </h3>
            <form onSubmit={handleInviteGroupSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-mono font-bold text-brand-soft uppercase mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={groupInviteEmail}
                  onChange={(e) => setGroupInviteEmail(e.target.value)}
                  placeholder="auditor@firm.com"
                  className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib"
                />
              </div>
              <div>
                <label className="block text-xs font-mono font-bold text-brand-soft uppercase mb-1">
                  Group Role
                </label>
                <select
                  value={groupInviteRole}
                  onChange={(e) => setGroupInviteRole(e.target.value as 'member' | 'viewer' | 'admin')}
                  className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib"
                >
                  <option value="viewer">Viewer (Read-only)</option>
                  <option value="member">Member (Can upload)</option>
                  <option value="admin">Admin (Can manage group)</option>
                </select>
              </div>
              <div className="flex items-center justify-end gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setInviteGroupId(null)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-brand-navy dark:text-white border border-brand-line dark:border-strokedark"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteGroupMemberMutation.isPending}
                  className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover"
                >
                  {inviteGroupMemberMutation.isPending ? 'Sending…' : 'Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: Generate API Key */}
      {isCreateApiKeyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-2xl p-6 sm:p-7 relative">
            <h3 className="text-lg font-bold text-brand-navy dark:text-white mb-1">
              Generate Developer API Key
            </h3>
            {createdKeySecret ? (
              <div className="space-y-4">
                <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                  ⚠️ <strong>Save this secret now:</strong> This full JWT token is displayed only once. It will not be shown again.
                </div>
                <div className="p-3 bg-brand-bg dark:bg-strokedark rounded-xl font-mono text-xs break-all select-all text-brand-navy dark:text-white border border-brand-line dark:border-strokedark">
                  {createdKeySecret}
                </div>
                <div className="flex items-center justify-between pt-3">
                  <span className="text-xs text-brand-green font-semibold">
                    {copiedKey ? '✓ Copied to clipboard!' : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(createdKeySecret);
                      setCopiedKey(true);
                      setTimeout(() => {
                        setCreatedKeySecret(null);
                        setCopiedKey(false);
                        setIsCreateApiKeyModalOpen(false);
                      }, 1500);
                    }}
                    className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover transition-colors cursor-pointer"
                  >
                    Copy & Close
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleCreateApiKeySubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-mono font-bold text-brand-soft uppercase mb-1">
                    Key Name
                  </label>
                  <input
                    type="text"
                    required
                    value={apiKeyName}
                    onChange={(e) => setApiKeyName(e.target.value)}
                    placeholder="e.g. Zapier Production Sync"
                    className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib"
                  />
                </div>
                <div>
                  <label className="block text-xs font-mono font-bold text-brand-soft uppercase mb-1">
                    Expiration Date
                  </label>
                  <input
                    type="date"
                    required
                    value={apiKeyExpiry}
                    onChange={(e) => setApiKeyExpiry(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib"
                  />
                </div>
                <div className="flex items-center justify-end gap-3 pt-3">
                  <button
                    type="button"
                    onClick={() => setIsCreateApiKeyModalOpen(false)}
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-brand-navy dark:text-white border border-brand-line dark:border-strokedark"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createApiKeyMutation.isPending}
                    className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover"
                  >
                    {createApiKeyMutation.isPending ? 'Generating…' : 'Generate Key'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* MODAL 5: Delete API Key Warning Dialog (Per API v2 §19) */}
      {keyToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="w-full max-w-md rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-2xl p-6 relative">
            <h3 className="text-lg font-bold text-rose-600 dark:text-rose-400 mb-2">
              Delete API Key ({keyToDelete.name})
            </h3>
            <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-xs text-rose-800 dark:text-rose-300 leading-relaxed mb-4">
              ⚠️ <strong>Security Notice:</strong> Deleting removes this key from your dashboard list, but the active JWT remains valid until its expiration date. If this key was leaked or compromised, deletion does not immediately invalidate active requests.
            </div>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setKeyToDelete(null)}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-brand-navy dark:text-white border border-brand-line dark:border-strokedark"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleteApiKeyMutation.isPending}
                onClick={handleDeleteApiKeyConfirm}
                className="px-5 py-2 rounded-xl text-xs font-semibold bg-rose-600 text-white hover:bg-rose-700 transition-colors"
              >
                {deleteApiKeyMutation.isPending ? 'Deleting…' : 'Confirm Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 6: Create MCP Token */}
      {isCreateMcpModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs">
          <div className="w-full max-w-lg rounded-2.5xl bg-white dark:bg-boxdark border border-brand-line dark:border-strokedark shadow-2xl p-6 relative">
            <h3 className="text-lg font-bold text-brand-navy dark:text-white mb-1">
              Create MCP Client Token
            </h3>
            {createdMcpSecret ? (
              <div className="space-y-4">
                <div className="p-3.5 rounded-xl bg-brand-green-tint border border-brand-green/30 text-xs text-[#0a6b4a] dark:text-emerald-300 leading-relaxed">
                  ✓ <strong>Token Created:</strong> Copy this MCP token string now. It will not be shown again.
                </div>
                <div className="p-3 bg-brand-bg dark:bg-strokedark rounded-xl font-mono text-xs break-all select-all text-brand-navy dark:text-white border border-brand-line dark:border-strokedark">
                  {createdMcpSecret}
                </div>
                <div className="flex items-center justify-end pt-3">
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(createdMcpSecret);
                      setCreatedMcpSecret(null);
                      setIsCreateMcpModalOpen(false);
                    }}
                    className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover cursor-pointer"
                  >
                    Copy Token & Close
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleCreateMcpTokenSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-mono font-bold text-brand-soft uppercase mb-1">
                    Token Name / Client Description
                  </label>
                  <input
                    type="text"
                    required
                    value={mcpTokenName}
                    onChange={(e) => setMcpTokenName(e.target.value)}
                    placeholder="e.g. Claude Desktop MacOS"
                    className="w-full px-4 py-2.5 rounded-xl bg-white dark:bg-form-input border border-brand-line dark:border-form-strokedark text-sm text-brand-navy dark:text-white focus:outline-none focus:border-brand-vib"
                  />
                </div>
                <div className="flex items-center justify-end gap-3 pt-3">
                  <button
                    type="button"
                    onClick={() => setIsCreateMcpModalOpen(false)}
                    className="px-4 py-2 rounded-xl text-xs font-semibold text-brand-navy dark:text-white border border-brand-line dark:border-strokedark"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createMcpTokenMutation.isPending}
                    className="px-5 py-2 rounded-xl text-xs font-semibold bg-brand-vib text-white hover:bg-brand-vib-hover"
                  >
                    {createMcpTokenMutation.isPending ? 'Creating…' : 'Create Token'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
